import { useEffect, useMemo, useState } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import {
  getUseProjectKey,
  useDeleteProjectSpecieType,
  useMutationWrapper,
  usePostProjectSpecieType,
  useProtocolSpeciesOptions,
  useStepperContext,
} from '@/hooks'
import { Animal, ProjectSpecie, ProjectSpecieBasic, Specie } from '@/types'
import { FooterActions, projectResearchSchema } from '@/features'
import { AutocompleteOption, ControlledAutocomplete } from '@/components/inputs'
import { useTranslation } from 'react-i18next'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'

type Species = AutocompleteOption<Specie>[]

type ResearchTypes = {
  [key: string]: AutocompleteOption[]
}

type ProjectResearchFormData = {
  species: Species
  research_types: ResearchTypes
}

type ProjectSpeciesFormContainerProps = {
  projectId: number
  projectSpeciesData: ProjectSpecie[]
  controls?: JSX.Element
  isPlanningCreated?: boolean
}

export const ProjectSpeciesFormContainer = ({
  projectId,
  projectSpeciesData,
  isPlanningCreated = false,
}: ProjectSpeciesFormContainerProps): JSX.Element => {
  const { t } = useTranslation()

  const queryClient = useQueryClient()

  const { stepBack, stepForward } = useStepperContext()

  const [apiCallError, setApiCallError] = useState<undefined | string>()

  const { speciesList, speciesOptions } =
    useProtocolSpeciesOptions<Specie>(true)

  const { mutateAsync: createProjectSpecie } = useMutationWrapper(() =>
    usePostProjectSpecieType(projectId)
  )

  const { mutateAsync: deleteProjectSpecie } = useMutationWrapper(() =>
    useDeleteProjectSpecieType(projectId)
  )

  const defaultValues = useMemo(() => {
    if (speciesList && projectSpeciesData) {
      const species = projectSpeciesData.reduce((acc, rs) => {
        if (!acc.some((specie) => specie.value.id === rs.specie.id)) {
          acc.push({
            value: {
              ...rs.specie,
              research_types:
                speciesList.find((s) => s.id === Number(rs.specie.id))
                  ?.research_types || [],
            },
            title: `[${rs.specie.animal.name}] ${rs.specie.name}`,
          })
        }

        return acc
      }, [] as Species)

      const researchTypes: ResearchTypes = {}

      species.forEach((specie) => {
        researchTypes[`${specie.value.id}`] = projectSpeciesData.reduce(
          (acc, rs) => {
            if (specie.value.id === rs.specie.id && rs.research_type) {
              acc.push({
                value: rs.research_type.id.toString(),
                title: rs.research_type.name,
              })
            }

            return acc
          },
          [] as AutocompleteOption[]
        )
      })

      return {
        species,
        research_types: researchTypes,
      }
    }

    return {
      species: [],
      research_types: {},
    }
  }, [speciesList, projectSpeciesData])

  const methods = useForm<ProjectResearchFormData>({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(projectResearchSchema),
  })

  useEffect(() => {
    methods.reset(defaultValues)
  }, [methods, defaultValues])

  const selectedSpecies = methods.watch(`species`)

  const selectedAnimals: Animal[] = useMemo(
    () =>
      selectedSpecies?.reduce((acc, value) => {
        if (!acc.some((item) => item.id === value.value.animal.id)) {
          acc.push({ ...value.value.animal })
        }

        return acc
      }, [] as Animal[]),
    [selectedSpecies]
  )

  const onSpeciesChange = (species: Species) => {
    const speciesIds = species.map((specie) => specie.value.id)
    const researchTypes = Object.keys(methods.getValues(`research_types`))

    speciesIds.forEach((id) => {
      if (!researchTypes.some((rt) => Number(rt) === id)) {
        methods.register(`research_types.${id}`, { value: [] })
      }
    })

    researchTypes.forEach((key) => {
      if (!speciesIds.includes(Number(key))) {
        methods.unregister(`research_types.${key}`)
      }
    })

    if (methods.getValues(`research_types`) === undefined) {
      methods.setValue(`research_types`, {})
    }
  }

  const saveData = async (formData: ProjectResearchFormData) => {
    const formResearchTypes = Object.keys(formData.research_types).flatMap(
      (specieId) =>
        formData.research_types[specieId].map((item) => ({
          research_type_id: item.value.toString(),
          specie_id: specieId,
        }))
    )

    try {
      const createdResearchTypes: Promise<ProjectSpecieBasic>[] = []

      formResearchTypes.forEach((researchType) => {
        if (
          !projectSpeciesData ||
          !projectSpeciesData.some(
            (rsp) =>
              rsp.research_type?.id.toString() ===
                researchType.research_type_id &&
              rsp.specie.id.toString() === researchType.specie_id
          )
        ) {
          createdResearchTypes.push(
            createProjectSpecie({
              research_type_id: Number(researchType.research_type_id),
              specie_id: researchType.specie_id,
            })
          )
        }
      })

      await Promise.all(createdResearchTypes)

      if (projectSpeciesData) {
        const deleteResearchTypes: Promise<unknown>[] = []

        projectSpeciesData.forEach((item) => {
          if (
            !formResearchTypes.some(
              (row) =>
                item.research_type?.id.toString() === row.research_type_id &&
                item.specie.id.toString() === row.specie_id
            )
          ) {
            item.id &&
              deleteResearchTypes.push(
                deleteProjectSpecie({
                  id: item.id,
                })
              )
          }
        })

        await Promise.all(deleteResearchTypes)

        const projectQueryKey = getUseProjectKey(projectId)

        await queryClient.invalidateQueries(projectQueryKey)
      }
    } catch (e) {
      setApiCallError(`${t('errors:api.execute')}: ${e}`)
    }
  }

  const handleSubmit = methods.handleSubmit(async (formData) => {
    try {
      await saveData(formData)
    } finally {
      stepForward()
    }
  })

  return (
    <Stack sx={{ paddingBottom: 0 }}>
      <FormProvider {...methods} data-testid="research-type-events-form">
        <ControlledAutocomplete
          name="species"
          control={methods.control}
          options={speciesOptions}
          isOptionEqualToValue={(option, value) =>
            option.value.id === value.value.id
          }
          onChangeCustom={(items) => onSpeciesChange(items as Species)}
          multiple={true}
          fullWidth={true}
          textFieldProps={{
            label: t('quotations:placeholders.species'),
          }}
          sx={{ mb: 4 }}
          disabled={isPlanningCreated}
        />
        <Stack>
          {selectedAnimals?.map(({ name, id }) => (
            <Stack key={id}>
              <Typography variant="h5" mb={2}>
                {name}
              </Typography>
              {selectedSpecies.map((specie) =>
                specie.value.animal.id === id ? (
                  <Stack
                    key={specie.value.id}
                    direction="row"
                    spacing={2}
                    sx={{
                      borderRadius: 2.5,
                      backgroundColor: 'grey.50',
                      p: 4,
                      mb: 4,
                    }}
                  >
                    <Box mt={2}>{specie.value.name}</Box>
                    <Box width="100%">
                      {specie.value.research_types && (
                        <ControlledAutocomplete
                          name={`research_types.${specie.value.id}`}
                          control={methods.control}
                          options={specie.value.research_types.map(
                            (researchType) => ({
                              value: researchType.id.toString(),
                              title: researchType.name,
                            })
                          )}
                          multiple={true}
                          fullWidth={true}
                          textFieldProps={{
                            label: t('quotations:placeholders.researchTypes'),
                          }}
                        />
                      )}
                    </Box>
                  </Stack>
                ) : null
              )}
            </Stack>
          ))}

          {!!apiCallError && (
            <Typography variant="body2" color="error.main">
              {apiCallError}
            </Typography>
          )}

          <FooterActions
            mutateInProcess={methods.formState.isSubmitting}
            isNextStepDisabled={
              methods.formState.isSubmitting || !methods.formState.isValid
            }
            onCancel={() => {
              stepBack()
            }}
            onSave={handleSubmit}
          />
        </Stack>
      </FormProvider>
    </Stack>
  )
}
